import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Context } from '../context/AuthContext';
import { getNav } from '../navigation/index';
import { ArrowLeftOnRectangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getAuth, signOut } from 'firebase/auth';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../utils/config';
import { getServerCount, listenForForegroundRefresh } from '../utils/firestoreCounts';

const AdminSideBar = ({ showSidebar, setShowSidebar }) => {
  const ADMIN_YELLOW = '#efb12f';

  const auth = getAuth();
  const { role, recentlySelectedCompany, user, handleLogout } = useContext(Context);
  const { pathname } = useLocation();
  const [navItemsByCategory, setNavItemsByCategory] = useState({});
  const [counts, setCounts] = useState({ leads: 0, messages: 0, errors: 0 });

  useEffect(() => {
    if (role) {
      const navs = getNav(role);
      setNavItemsByCategory(navs);
    }
  }, [role]);

  // Build queries only when we have what we need (prevents undefined query refs)
  const leadsQuery = useMemo(() => {
    if (!recentlySelectedCompany || !user) return null;
    // NOTE: adjust collection/path/filters to match your schema
    // This is a safe default based on typical patterns.
    return query(
      collection(db, 'leads'),
      where('companyId', '==', recentlySelectedCompany?.id || recentlySelectedCompany),
      where('status', '==', 'new')
    );
  }, [recentlySelectedCompany, user]);

  const messagesQuery = useMemo(() => {
    if (!recentlySelectedCompany || !user) return null;
    // NOTE: adjust collection/path/filters to match your schema
    return query(
      collection(db, 'messages'),
      where('companyId', '==', recentlySelectedCompany?.id || recentlySelectedCompany),
      where('unread', '==', true)
    );
  }, [recentlySelectedCompany, user]);

  const errorsQuery = useMemo(() => {
    if (role !== 'Admin' || !user) return null;

    return query(
      collection(db, 'appErrors'),
      where('status', '==', 'New')
    );
  }, [role, user]);

  useEffect(() => {
    if (!recentlySelectedCompany || !user || !leadsQuery || !messagesQuery) {
      setCounts((prev) => ({ ...prev, leads: 0, messages: 0 }));
      return undefined;
    }

    let cancelled = false;
    const loadCounts = async () => {
      try {
        const [leads, messages] = await Promise.all([
          getServerCount(leadsQuery),
          getServerCount(messagesQuery),
        ]);

        if (!cancelled) setCounts((prev) => ({ ...prev, leads, messages }));
      } catch (error) {
        console.error('Error loading admin sidebar counts:', error);
        if (!cancelled) setCounts((prev) => ({ ...prev, leads: 0, messages: 0 }));
      }
    };

    loadCounts();
    const removeForegroundRefresh = listenForForegroundRefresh(loadCounts);

    return () => {
      cancelled = true;
      removeForegroundRefresh();
    };
  }, [recentlySelectedCompany, user, leadsQuery, messagesQuery]);

  useEffect(() => {
    if (!errorsQuery) return undefined;

    let cancelled = false;
    const loadErrorCount = async () => {
      try {
        const errors = await getServerCount(errorsQuery);
        if (!cancelled) setCounts((prev) => ({ ...prev, errors }));
      } catch (error) {
        console.error('Error loading app error count:', error);
        if (!cancelled) setCounts((prev) => ({ ...prev, errors: 0 }));
      }
    };

    loadErrorCount();
    const removeForegroundRefresh = listenForForegroundRefresh(loadErrorCount);

    return () => {
      cancelled = true;
      removeForegroundRefresh();
    };
  }, [errorsQuery]);

  const logout = async () => {
    try {
      await signOut(auth);
      handleLogout();
    } catch (error) {
      console.error('Logout failed:', error.message);
    }
  };

  const getPath = (itemPath) => itemPath;

  return (
    <div>
      {/* Overlay for mobile view */}
      <div
        onClick={() => setShowSidebar(false)}
        className={`fixed duration-200 lg:hidden ${
          showSidebar ? 'w-screen h-screen bg-black/70 top-0 left-0 z-10' : 'w-0'
        }`}
      ></div>

      {/* Sidebar */}
      <div
        className={`app-mobile-sidebar-shell w-full lg:w-[16.25rem] fixed z-50 top-0 h-screen shadow-2xl transition-all
          bg-slate-950 text-slate-100 border-r border-slate-800/60
          ${showSidebar ? 'left-0' : '-left-full lg:left-0'}`}
      >
        <div className="flex h-full flex-col overflow-hidden">
          {/* Header */}
          <div className="h-[5.9375rem] flex items-center justify-between border-b border-slate-800/60 px-4 shrink-0 lg:justify-center">
            <Link
              to="/"
              className="text-2xl font-extrabold tracking-tight transition"
              style={{ color: ADMIN_YELLOW }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Drip Drop <span className="text-slate-300">[Admin]</span>
            </Link>
            <button
              type="button"
              onClick={() => setShowSidebar(false)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-700 text-slate-200 transition hover:bg-slate-900 lg:hidden"
              aria-label="Close navigation"
              title="Close navigation"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="min-h-0 flex-grow overflow-y-auto overscroll-contain px-2 pt-5 text-slate-200">
            {Object.keys(navItemsByCategory).map((category) => (
              <div
                key={category}
                className={`mb-4 ${category !== 'NA' ? 'border-t border-slate-800/60 pt-3' : ''}`}
              >
                {category !== 'NA' && (
                  <h3 className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                    {category}
                  </h3>
                )}

                <ul className="flex flex-col gap-1">
                  {navItemsByCategory[category].map((item) => {
                    const isActive = pathname.toLowerCase() === item.path.toLowerCase();
                    const count =
                      item.title === 'Leads'
                        ? counts.leads
                        : item.title === 'Messages'
                        ? counts.messages
                        : item.title === 'Errors'
                        ? counts.errors
                        : 0;

                    return (
                      <li key={`${item.path}-${item.title}`}>
                        <Link
                          to={getPath(item.path)}
                          className={`group w-full px-3 py-2 rounded-md flex justify-between items-center gap-3 font-medium transition-all
                            ${
                              isActive
                                ? `bg-slate-900/80 ring-1 ring-[#efb12f]/30 shadow-[0_0_0_1px_rgba(239,177,47,0.10)]`
                                : 'hover:bg-slate-900/60 text-slate-200'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`w-6 h-6 transition ${
                                isActive ? '' : 'text-slate-400 group-hover:text-slate-200'
                              }`}
                              style={isActive ? { color: ADMIN_YELLOW } : undefined}
                            >
                              {item.icon}
                            </span>

                            <span
                              className="transition"
                              style={isActive ? { color: ADMIN_YELLOW } : undefined}
                            >
                              {item.title}
                            </span>
                          </div>

                          {count > 0 && (
                            <span
                              className={`text-xs font-semibold px-2.5 py-0.5 rounded-full
                                bg-[#efb12f]/15 text-[#efb12f] ring-1 ring-[#efb12f]/30`}
                            >
                              {count}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

          {/* Logout Button */}
          <div className="app-sidebar-mobile-footer-safe p-4 border-t border-slate-800/60 shrink-0">
            <button
              onClick={logout}
              className="w-full flex items-center px-4 py-3 text-left rounded-lg font-medium transition
                text-red-300 hover:text-red-200 hover:bg-red-500/10 ring-1 ring-transparent hover:ring-red-500/20"
            >
              <ArrowLeftOnRectangleIcon className="w-6 h-6 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSideBar;
